import React, { useState } from "react";
import { Box, Typography, IconButton, useTheme, Menu, MenuItem, Chip, Divider } from "@mui/material";
import TemplateSendPreview from "../Inbox/Features/TemplateSendFeatures/TemplateSendPreview";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ListIcon from "@mui/icons-material/List";
import { Handle, Position } from "reactflow";
import ImageIcon from "@mui/icons-material/Image";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import InventoryIcon from "@mui/icons-material/Inventory";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";

// Add CSS animation for pulse effect (green for action selection)
const pulseAnimation = `
  @keyframes pulse {
    0% {
      box-shadow: 0 0 15px #4caf50, 0 0 30px #4caf50;
    }
    50% {
      box-shadow: 0 0 20px #4caf50, 0 0 40px #4caf50;
    }
    100% {
      box-shadow: 0 0 15px #4caf50, 0 0 30px #4caf50;
    }
  }
  @keyframes pulseRed {
    0% {
      box-shadow: 0 0 15px #f44336, 0 0 30px #f44336;
    }
    50% {
      box-shadow: 0 0 20px #f44336, 0 0 40px #f44336;
    }
    100% {
      box-shadow: 0 0 15px #f44336, 0 0 30px #f44336;
    }
  }
`;

// Inject the CSS animation
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = pulseAnimation;
  document.head.appendChild(style);
}
const WorkflowNode = ({ data, onDelete, onHover }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [anchorEl, setAnchorEl] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  
  // Check if we're in action selection mode
  const [isInActionSelectionMode, setIsInActionSelectionMode] = useState(false);
  
  // Check if we're in keyword target selection mode
  const [isInKeywordSelectionMode, setIsInKeywordSelectionMode] = useState(false);
  
  React.useEffect(() => {
    const checkSelectionMode = () => {
      if (window.isActionSelectionMode && typeof window.isActionSelectionMode === 'function') {
        const isInMode = window.isActionSelectionMode();
        setIsInActionSelectionMode(isInMode);
      } else {
        setIsInActionSelectionMode(false);
      }
      
      if (window.isKeywordTargetSelectionMode && typeof window.isKeywordTargetSelectionMode === 'function') {
        const isInKeywordMode = window.isKeywordTargetSelectionMode();
        setIsInKeywordSelectionMode(isInKeywordMode);
      } else {
        setIsInKeywordSelectionMode(false);
      }
    };
    
    const interval = setInterval(checkSelectionMode, 100);
    return () => clearInterval(interval);
  }, []);

  const {
    heading = "Contact sends first incoming message",
    description = "Triggered when the first message arrives",
    icon,
    color = "#B2F2D6",
    type,
    isOptionNode = false, // New prop to identify option nodes
    isAfterOptionNode = false, // New prop to identify if this node comes after an option node
    isReadOnly = false, // New prop to identify read-only mode
    isBeforeEndNode = false, // New prop to identify if this node is before END node
    config, // Add config to detect go-to-action
  } = data;

  // Helper function to check if this is a "go to action" node
  const isGoToActionNode = React.useMemo(() => {
    if (type !== 'action' || !config || !Array.isArray(config)) return false;
    
    for (const section of config) {
      if (section.fields && Array.isArray(section.fields)) {
        for (const field of section.fields) {
          if (field.type === 'actions' && field.selectedValue) {
            // Check if selectedValue is an object with branchId and nodeIndex
            if (typeof field.selectedValue === 'object' && 
                field.selectedValue.branchId && 
                field.selectedValue.nodeIndex !== undefined) {
              return true;
            }
          }
        }
      }
    }
    return false;
  }, [type, config]);

  // Helper function to check if this action is from the same branch as the current selection context
  const isFromSameBranch = React.useMemo(() => {
    if (!isInActionSelectionMode || type !== 'action') return false;
    
    const currentBranchId = window.getCurrentSelectionBranchId && window.getCurrentSelectionBranchId();
    const nodeBranchId = data.branchId;
    
    return currentBranchId && nodeBranchId && currentBranchId === nodeBranchId;
  }, [isInActionSelectionMode, type, data.branchId]);

  const handleMenuClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDelete = () => {
    if (onDelete && data.branchId && data.nodeId !== undefined) {
      onDelete({ branchId: data.branchId, nodeId: data.nodeId });
    } else {
      // Dummy fallback
      alert('Delete functionality will be implemented soon!');
    }
    handleMenuClose();
  };

  // Helper function to extract button text from config
  const extractButtonTextFromConfig = React.useMemo(() => {
    if (!config || !Array.isArray(config)) return null;
    for (const section of config) {
      if (section.fields && Array.isArray(section.fields)) {
        for (const field of section.fields) {
          // Check for Button Title field (can be type 'text' or 'variable')
          if ((field.label === 'Button Title' || field.label === 'Title') && field.selectedValue) {
            return field.selectedValue;
          }
        }
      }
    }
    return null;
  }, [config]);

  // If this is an option node, render a compact style
  if (isOptionNode) {
    // For button reply triggers, prefer button text from config if heading is still "button reply" or "Trigger for button reply"
    const displayHeading = (extractButtonTextFromConfig && 
                           (heading === 'button reply' || heading === 'Trigger for button reply' || heading === 'Button'))
                           ? extractButtonTextFromConfig 
                           : heading;
    
    return (
      <Box
        sx={{
          width: 120, // Smaller width for option nodes
          height: 40, // Fixed height for compact look
          borderRadius: 2,
          boxShadow: isHovered ? 4 : 1,
          bgcolor: "#E8E8E8", // Light gray background
          border: "1px solid #ccc", // Same border for all option nodes
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          fontFamily: "inherit",
          zIndex: isHovered ? 9999 : 'auto',
          transform: isHovered ? 'scale(1.05)' : 'scale(1)',
          transition: 'all 0.2s ease-in-out',
          cursor: 'pointer',
        }}
        onMouseEnter={() => {
          setIsHovered(true);
          if (onHover) onHover(data.id || 'unknown');
        }}
        onMouseLeave={() => {
          setIsHovered(false);
          if (onHover) onHover(null);
        }}
      >
        {/* Handles for edge connections */}
        <Handle
          type="target"
          position={Position.Top}
          style={{ background: "transparent", border: "none" }}
        />
        <Handle
          type="source"
          position={Position.Bottom}
          style={{ background: "transparent", border: "none" }}
        />

        {/* Display trigger information for option nodes */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', px: 1 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: "#333",
              fontSize: 12,
              textAlign: "center",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {displayHeading}
          </Typography>
          {/* Show trigger config if available (but not for button title fields, as they're already shown in heading) */}
          {data.config && Array.isArray(data.config) && data.config.length > 0 && (
            <Box sx={{ mt: 0.5 }}>
              {data.config.map((section, sectionIdx) =>
                Array.isArray(section.fields)
                  ? section.fields
                    .filter(field => 
                      field.selectedValue && 
                      field.label !== 'Button Title' && 
                      field.label !== 'Title' // Don't show button title again as it's in the heading
                    )
                    .map((field, idx) => (
                      <Typography
                        key={`option-value-${sectionIdx}-${idx}`}
                        sx={{
                          color: "#666",
                          fontSize: 10,
                          fontWeight: 500,
                          textAlign: "center",
                        }}
                      >
                        {field.selectedValue}
                      </Typography>
                    ))
                  : null
              )}
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  // If this is a "go to action" node, render a compact style similar to option nodes
  if (isGoToActionNode) {
    return (
      <>
        <Box
          sx={{
            width: 140, // Slightly wider than option nodes
            height: 40, // Same height as option nodes
            borderRadius: 2,
            boxShadow: isHovered ? 4 : 1,
            bgcolor: "#fff3e0", // Light orange background to distinguish from option nodes
            border: "2px solid #ff9800", // Orange border
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            fontFamily: "inherit",
            zIndex: isHovered ? 9999 : 'auto',
            transform: isHovered ? 'scale(1.05)' : 'scale(1)',
            transition: 'all 0.2s ease-in-out',
            cursor: 'pointer',
          }}
          onMouseEnter={() => {
            setIsHovered(true);
            if (onHover) onHover(data.id || 'unknown');
          }}
          onMouseLeave={() => {
            setIsHovered(false);
            if (onHover) onHover(null);
          }}
        >
          {/* Handles for edge connections */}
          <Handle
            type="target"
            position={Position.Top}
            style={{ background: "transparent", border: "none" }}
          />
          <Handle
            type="source"
            position={Position.Bottom}
            style={{ background: "transparent", border: "none" }}
          />

          {/* Display just the title */}
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              color: "#e65100",
              fontSize: 12,
              textAlign: "center",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              flex: 1,
            }}
          >
            {heading}
          </Typography>
          
          {/* Three dots menu for deletion */}
          <IconButton
            size="small"
            sx={{ 
              color: "#e65100", 
              p: 0.5,
              ml: 0.5,
              '&:hover': {
                bgcolor: 'rgba(230, 81, 0, 0.1)'
              }
            }}
            onClick={handleMenuClick}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>
        
        {/* Three dots menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          onClick={(e) => e.stopPropagation()}
        >
          <MenuItem onClick={handleDelete}>
            Delete
          </MenuItem>
        </Menu>
      </>
    );
  }

  // If this is an action node, use pastel green header
  const headerBg =
    type === "action"
      ? "#B2F2D6"
      : isDark
        ? color || "#2e4d44"
        : color || "#B2F2D6";
  const headerText = isDark
    ? "#0e4630"
    : type === "action"
      ? "#13795B"
      : "#13795B";
  const nodeBg = isDark ? "#23272b" : "#fff";
  const borderColor = isDark ? "#444" : "#e0e0e0";
  const descColor = isDark ? "#eee" : "#444";

  return (
    <Box
      sx={{
        width: 260,
        borderRadius: 2,
        boxShadow: isHovered ? 6 : 2,
        bgcolor: nodeBg,
        overflow: "hidden",
        border: `1.5px solid ${borderColor}`,
        fontFamily: "inherit",
        position: "relative",
        zIndex: isHovered ? 1000 : 1, // Ensure nodes have a base z-index to appear above edges
        transform: isHovered ? 'scale(1.05)' : 'scale(1)',
        transition: 'all 0.2s ease-in-out',
        cursor: 'grab',
        display: "flex",
        flexDirection: "column",
        ...(isAfterOptionNode && { right: 30 }), // Add right positioning if after option node
        ...(isReadOnly && {
          cursor: 'default',
          pointerEvents: 'none',
          opacity: 0.9
        }), // Disable interactions in read-only mode
        // Add glowing border for selectable action nodes in action selection mode (green)
        ...(isInActionSelectionMode && type === 'action' && !isFromSameBranch && {
          border: `2px solid ${isDark ? '#4caf50' : '#4caf50'}`,
          boxShadow: `0 0 15px ${isDark ? '#4caf50' : '#4caf50'}, 0 0 30px ${isDark ? '#4caf50' : '#4caf50'}`,
          animation: 'pulse 2s infinite',
          cursor: 'pointer',
          '&:hover': {
            boxShadow: `0 0 20px ${isDark ? '#66bb6a' : '#66bb6a'}, 0 0 40px ${isDark ? '#66bb6a' : '#66bb6a'}`,
            transform: 'scale(1.02)',
            transition: 'all 0.2s ease-in-out'
          }
        }),
        // Add glowing border for selectable action nodes in keyword selection mode (red)
        ...(isInKeywordSelectionMode && type === 'action' && {
          border: `2px solid ${isDark ? '#f44336' : '#f44336'}`,
          boxShadow: `0 0 15px ${isDark ? '#f44336' : '#f44336'}, 0 0 30px ${isDark ? '#f44336' : '#f44336'}`,
          animation: 'pulseRed 2s infinite',
          cursor: 'pointer',
          '&:hover': {
            boxShadow: `0 0 20px ${isDark ? '#ef5350' : '#ef5350'}, 0 0 40px ${isDark ? '#ef5350' : '#ef5350'}`,
            transform: 'scale(1.02)',
            transition: 'all 0.2s ease-in-out'
          }
        }),
        // Add dimming for actions from the same branch in action selection mode
        ...(isInActionSelectionMode && type === 'action' && isFromSameBranch && {
          opacity: 0.4,
          filter: 'grayscale(50%)',
          cursor: 'not-allowed',
          border: `2px solid ${isDark ? '#999' : '#999'}`
        }),
        // Add subtle dimming for non-selectable nodes in action selection mode
        ...(isInActionSelectionMode && type !== 'action' && {
          opacity: 0.6,
          filter: 'grayscale(30%)',
          cursor: 'not-allowed'
        }),
        // Add subtle dimming for non-selectable nodes in keyword selection mode
        ...(isInKeywordSelectionMode && type !== 'action' && {
          opacity: 0.6,
          filter: 'grayscale(30%)',
          cursor: 'not-allowed'
        })
      }}
      onMouseEnter={() => {
        setIsHovered(true);
        if (onHover) onHover(data.id || 'unknown');
      }}
      onMouseLeave={() => {
        setIsHovered(false);
        if (onHover) onHover(null);
      }}
    >
      {/* Handles for edge connections */}
      <Handle
        type="target"
        position={Position.Top}
        style={{ background: "transparent", border: "none" }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        style={{ background: "transparent", border: "none" }}
      />
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          bgcolor: headerBg,
          px: 1,
          py: 0.5,
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          flexShrink: 0,
        }}
      >
        {icon || <ChatBubbleOutlineIcon sx={{ color: headerText, mr: 1 }} />}
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            color: headerText,
            flex: 1,
            fontSize: 10,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {heading}
        </Typography>
        {data.buttonText ? null : !isReadOnly && type === "action" ? (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {/* Selection indicator for action nodes in action selection mode (green) */}
            {isInActionSelectionMode && (
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: isDark ? '#4caf50' : '#4caf50',
                  mr: 0.5,
                  animation: 'pulse 1s infinite',
                  boxShadow: `0 0 5px ${isDark ? '#4caf50' : '#4caf50'}`
                }}
              />
            )}
            {/* Selection indicator for action nodes in keyword selection mode (red) */}
            {isInKeywordSelectionMode && (
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: isDark ? '#f44336' : '#f44336',
                  mr: 0.5,
                  animation: 'pulseRed 1s infinite',
                  boxShadow: `0 0 5px ${isDark ? '#f44336' : '#f44336'}`
                }}
              />
            )}
          <IconButton
            size="small"
            sx={{ color: headerText, p: 0.5 }}
            onClick={handleMenuClick}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
          </Box>
        ) : null}
      </Box>
      {/* Description and button for new content */}
      <Box sx={{ 
        px: 1, 
        py: 1, 
        pb: isBeforeEndNode ? 3.5 : 2, // Add extra bottom padding if before END node to prevent overlap
        bgcolor: nodeBg, 
        textAlign: "center", 
        maxWidth: "100%", 
        width: "100%",
        minHeight: "auto",
        overflowY: "visible",
        overflowX: "hidden",
        position: "relative",
        boxSizing: "border-box",
      }}>
        {type !== "action" && (
          <Typography
            variant="body2"
            sx={{ color: descColor, fontSize: 12, mb: data.buttonText ? 1 : 0 }}
          >
            {description}
          </Typography>
        )}
        {/* For action nodes, display config field values (supporting config as array of sections with fields) */}
        {type === "action" &&
          Array.isArray(data.config) &&
          data.config.length > 0 && (
            <>
              {/* Render all heading fields at the top (not for list message or interactive reply buttons) */}
              {heading !== 'Send list message' && heading !== 'Send interactive reply buttons message' && data.config.map((section, sectionIdx) =>
                Array.isArray(section.fields)
                  ? section.fields
                    .filter((field) => field.type === "heading" && field.selectedValue)
                    .map((field, idx) => (
                      <Typography
                        key={`heading-${sectionIdx}-${idx}`}
                        sx={{ 
                          fontWeight: 700, 
                          fontSize: 15, 
                          mb: 1, 
                          color: descColor,
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                          maxWidth: "100%",
                          overflow: "hidden",
                        }}
                      >
                        {field.selectedValue}
                      </Typography>
                    ))
                  : null
              )}
              {heading === 'Send list message' ? (() => {
                let headerValue = null;
                let bodyValue = null;
                for (const section of data.config) {
                  if (!Array.isArray(section.fields)) continue;
                  for (const field of section.fields) {
                    const lbl = (field.label || '').toLowerCase();
                    if (!headerValue && lbl === 'message header' && field.selectedValue) headerValue = field.selectedValue;
                    if (!bodyValue && lbl === 'message body' && field.selectedValue) bodyValue = field.selectedValue;
                  }
                }
                const words = bodyValue ? bodyValue.trim().split(/\s+/) : [];
                const displayBody = words.length > 100 ? words.slice(0, 100).join(' ') + ' more...' : bodyValue;
                return (
                  <Box sx={{ maxWidth: "100%", width: "100%" }}>
                    {headerValue && (
                      <Typography sx={{ fontWeight: 700, fontSize: 13, color: descColor, mb: 0.5, textAlign: 'center', wordBreak: "break-word", overflowWrap: "break-word", width: "100%" }}>
                        {headerValue}
                      </Typography>
                    )}
                    {displayBody && (
                      <Typography sx={{ color: descColor, fontSize: 13, whiteSpace: "pre-line", wordBreak: "break-word", overflowWrap: "break-word", maxWidth: "100%", textAlign: "left", mt: 0.5 }}>
                        {displayBody}
                      </Typography>
                    )}
                  </Box>
                );
              })() : heading === 'Send interactive reply buttons message' ? (() => {
                let headerValue = null;
                let bodyValue = null;
                const buttonTitles = [];
                for (const section of data.config) {
                  if (!Array.isArray(section.fields)) continue;
                  for (const field of section.fields) {
                    const lbl = (field.label || '').toLowerCase();
                    if (!bodyValue && lbl === 'body' && field.type === 'text large' && field.selectedValue) bodyValue = field.selectedValue;
                    if (!headerValue && lbl === 'header type' && field.type === 'select' && field.selectedValue === 'Text' && Array.isArray(field.options)) {
                      const textOpt = field.options.find(o => o.label === 'Text' || o.value === 'text');
                      if (textOpt && Array.isArray(textOpt.subfields)) {
                        const htf = textOpt.subfields.find(f => f.label === 'Header text');
                        if (htf && htf.selectedValue) headerValue = htf.selectedValue;
                      }
                    }
                    if (lbl.includes('button') && Array.isArray(field.subfields)) {
                      const titleField = field.subfields.find(f =>
                        (f.label === 'Button Title' || f.label === 'Title') && f.selectedValue
                      );
                      if (titleField) buttonTitles.push(titleField.selectedValue);
                    }
                  }
                }
                const words = bodyValue ? bodyValue.trim().split(/\s+/) : [];
                const displayBody = words.length > 100 ? words.slice(0, 100).join(' ') + ' more...' : bodyValue;
                return (
                  <Box sx={{ maxWidth: "100%", width: "100%" }}>
                    {headerValue && (
                      <Typography sx={{ fontWeight: 800, fontSize: 13, color: descColor, mb: 0.5, textAlign: 'center', wordBreak: "break-word", overflowWrap: "break-word", width: "100%" }}>
                        {headerValue}
                      </Typography>
                    )}
                    {displayBody && (
                      <Typography sx={{ color: descColor, fontSize: 12, whiteSpace: "pre-line", wordBreak: "break-word", overflowWrap: "break-word", maxWidth: "100%", textAlign: "left", mt: headerValue ? 0.5 : 0 }}>
                        {displayBody}
                      </Typography>
                    )}
                    {buttonTitles.length > 0 && (
                      <Box sx={{ mt: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, width: "100%" }}>
                        {buttonTitles.map((title, i) => (
                          <Box key={i} sx={{
                            border: `1px solid ${isDark ? '#66bb6a' : '#2e7d32'}`,
                            borderRadius: 1,
                            py: 0.4,
                            px: 1,
                            textAlign: 'center',
                            color: isDark ? '#66bb6a' : '#2e7d32',
                            fontWeight: 600,
                            fontSize: 11,
                            wordBreak: "break-word",
                            width: "70%",
                            boxSizing: "border-box",
                          }}>
                            {title}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                );
              })() : heading === 'Set Variable' ? (() => {
                // Collect all variable names from both new (Variables array) and legacy (Variable name field) formats
                const varNames = [];
                for (const section of data.config) {
                  if (!Array.isArray(section.fields)) continue;
                  for (const field of section.fields) {
                    if (field.label === 'Variables' && Array.isArray(field.selectedValue)) {
                      field.selectedValue.forEach(v => { if (v.name) varNames.push(v.name); });
                    } else if (field.label === 'Variable name' && field.selectedValue) {
                      varNames.push(field.selectedValue);
                    }
                  }
                }
                return (
                  <Box sx={{ textAlign: 'center', mt: 0.5, px: 1 }}>
                    {varNames.length > 0 ? varNames.map((name, i) => (
                      <Typography key={i} sx={{ color: descColor, fontSize: 12 }}>
                        <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{`{{${name}}}`}</span>
                      </Typography>
                    )) : null}
                  </Box>
                );
              })() : heading === 'Branch: Business Hours' ? (() => {
                let timezone = null;
                let enabledDayCount = 0;
                for (const section of data.config) {
                  if (!Array.isArray(section.fields)) continue;
                  for (const field of section.fields) {
                    if (field.type === 'businessHours' && field.selectedValue) {
                      timezone = field.selectedValue.timezone || null;
                      const schedule = field.selectedValue.schedule || {};
                      enabledDayCount = Object.values(schedule).filter((d) => d?.enabled).length;
                    }
                  }
                }
                return (
                  <Box sx={{ textAlign: 'center', mt: 0.5, px: 1 }}>
                    {timezone && (
                      <Typography sx={{ color: descColor, fontSize: 12 }}>{timezone}</Typography>
                    )}
                    <Typography sx={{ color: descColor, fontSize: 12, mt: 0.25 }}>
                      {enabledDayCount} day{enabledDayCount === 1 ? '' : 's'} configured
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 0.75 }}>
                      <Box sx={{ border: `1px solid ${isDark ? '#66bb6a' : '#2e7d32'}`, borderRadius: 1, px: 1, py: 0.25, color: isDark ? '#66bb6a' : '#2e7d32', fontSize: 11, fontWeight: 600 }}>YES</Box>
                      <Box sx={{ border: `1px solid ${isDark ? '#ef5350' : '#c62828'}`, borderRadius: 1, px: 1, py: 0.25, color: isDark ? '#ef5350' : '#c62828', fontSize: 11, fontWeight: 600 }}>NO</Box>
                    </Box>
                  </Box>
                );
              })() : heading === 'Branch: Contact Fields' ? (() => {
                const value = data.config
                  ?.flatMap((section) => section.fields || [])
                  .find((field) => field.type === 'contactFieldsCondition')?.selectedValue;
                const count = Array.isArray(value?.fieldIds) ? value.fieldIds.length : 0;
                return (
                  <Box sx={{ textAlign: 'center', mt: 0.5, px: 1 }}>
                    <Typography sx={{ color: descColor, fontSize: 12 }}>
                      {count} field{count === 1 ? '' : 's'} · {value?.matchMode === 'any' ? 'any' : 'all'} must have a value
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 0.75 }}>
                      <Box sx={{ border: `1px solid ${isDark ? '#66bb6a' : '#2e7d32'}`, borderRadius: 1, px: 1, py: 0.25, color: isDark ? '#66bb6a' : '#2e7d32', fontSize: 11, fontWeight: 600 }}>YES</Box>
                      <Box sx={{ border: `1px solid ${isDark ? '#ef5350' : '#c62828'}`, borderRadius: 1, px: 1, py: 0.25, color: isDark ? '#ef5350' : '#c62828', fontSize: 11, fontWeight: 600 }}>NO</Box>
                    </Box>
                  </Box>
                );
              })() : heading === 'Assign conversation' ? (() => {
                let assignStyle = null;
                let assignName = null;
                let autoClose = false;
                let autoCloseDuration = null;
                let autoCloseType = null;

                for (const section of data.config) {
                  if (section.name && section.name.includes('Assign conversation to')) {
                    const styleField = section.fields?.find(f => f.label === 'Select Assignment Style');
                    if (styleField?.selectedValue) {
                      assignStyle = styleField.selectedValue;
                      if (assignStyle === 'Round Robin (All)') {
                        assignName = 'Round Robin';
                      } else if (assignStyle === 'Round Robin (specific members)') {
                        assignName = 'Round Robin';
                      } else {
                        const currentAssignStyle = assignStyle;
                        const selectedOption = styleField.options?.find(o => o.label === currentAssignStyle);
                        if (selectedOption?.subfields) {
                          for (const sub of selectedOption.subfields) {
                            if (sub.selectedNames && sub.selectedNames.length > 0) {
                              assignName = sub.selectedNames[0];
                              break;
                            }
                            if (!assignName && sub.selectedValues && sub.selectedValues.length > 0) {
                              assignName = sub.selectedValues[0];
                            }
                          }
                        }
                      }
                    }
                  }
                  if (section.name === 'Auto-close after assignment') {
                    const enableField = section.fields?.find(f => f.label === 'Enable auto-close');
                    if (enableField?.selectedValue === true) {
                      autoClose = true;
                      const durationField = section.fields?.find(f => f.label === 'Select Interval Duration');
                      const typeField = section.fields?.find(f => f.label === 'Select Duration Type');
                      autoCloseDuration = durationField?.selectedValue;
                      autoCloseType = typeField?.selectedValue;
                    }
                  }
                }

                return (
                  <Box sx={{ textAlign: 'left', px: 0.5, py: 0.5 }}>
                    {assignName ? (
                      <Typography sx={{ color: descColor, fontSize: 12 }}>
                        <span style={{ fontWeight: 600 }}>Assign to:</span> {assignName}
                      </Typography>
                    ) : assignStyle ? (
                      <Typography sx={{ color: descColor, fontSize: 12 }}>
                        <span style={{ fontWeight: 600 }}>Assign to:</span> {assignStyle}
                      </Typography>
                    ) : null}
                    {autoClose && autoCloseDuration != null && (
                      <Typography sx={{ color: descColor, fontSize: 12, mt: 0.5 }}>
                        Auto closed after {autoCloseDuration} {autoCloseType}
                      </Typography>
                    )}
                  </Box>
                );
              })() : <Box sx={{ maxWidth: "100%", width: "100%" }}>
                {data.config.map((section, sectionIdx) =>
                  Array.isArray(section.fields)
                    ? section.fields.map((field, idx) => {
                      if (
                        field.type === "number" &&
                        field.selectedValue !== undefined &&
                        field.selectedValue !== ""
                      ) {
                        return (
                          <Typography
                            key={sectionIdx + "-" + idx}
                            sx={{
                              color: descColor,
                              fontSize: 13,
                              fontWeight: 500,
                            }}
                          >
                            {field.selectedValue}
                          </Typography>
                        );
                      }
                      if (
                        field.type === "text large" &&
                        field.selectedValue
                      ) {
                        return (
                          <Typography
                            key={sectionIdx + "-" + idx}
                            sx={{
                              color: descColor,
                              fontSize: 13,
                              whiteSpace: "pre-line",
                              wordBreak: "break-word",
                              overflowWrap: "break-word",
                              maxWidth: "100%",
                              overflow: "hidden",
                              textAlign: "left",
                              mb: 1, // Add bottom margin to prevent overlap
                            }}
                          >
                            {field.selectedValue}
                          </Typography>
                        );
                      }
                      if (field.type === "select" && field.selectedValue) {
                        return (
                          <Typography
                            key={sectionIdx + "-" + idx}
                            sx={{ 
                              color: descColor, 
                              fontSize: 13,
                              wordBreak: "break-word",
                              overflowWrap: "break-word",
                              maxWidth: "100%",
                              overflow: "hidden",
                              textAlign: "left",
                            }}
                          >
                            {field.selectedValue}
                          </Typography>
                        );
                      }
                      if (field.type === "text" && field.selectedValue) {
                        return (
                          <Typography
                            key={sectionIdx + "-" + idx}
                            sx={{ 
                              color: descColor, 
                              fontSize: 13,
                              wordBreak: "break-word",
                              overflowWrap: "break-word",
                              maxWidth: "100%",
                              overflow: "hidden",
                              textAlign: "left",
                            }}
                          >
                            {field.selectedValue}
                          </Typography>
                        );
                      }
                      if (field.type === "apiIntegrationForm" && field.selectedValue) {
                        const cfg = field.selectedValue;
                        return (
                          <Box key={sectionIdx + "-" + idx} sx={{ width: "100%", textAlign: "left" }}>
                            {cfg.name && (
                              <Typography sx={{ fontWeight: 700, fontSize: 13, color: descColor, wordBreak: "break-word", overflowWrap: "break-word", textAlign: "center" }}>
                                {cfg.name}
                              </Typography>
                            )}
                            {cfg.description && (
                              <Typography sx={{ fontSize: 12, color: descColor, wordBreak: "break-word", overflowWrap: "break-word", mt: 0.5 }}>
                                {cfg.description}
                              </Typography>
                            )}
                          </Box>
                        );
                      }
                      if (field.type === "catalog" && field.selectedValue && typeof field.selectedValue === 'object') {
                        const cat = field.selectedValue;
                        const catProducts = Array.isArray(cat.products) ? cat.products : [];
                        const isMulti = catProducts.length > 1;
                        return (
                          <Box key={sectionIdx + "-" + idx} sx={{ width: "100%", mt: 0.5 }}>
                            {/* Catalog header */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
                              <ShoppingCartIcon sx={{ color: '#25D366', fontSize: 16 }} />
                              <Typography sx={{ fontWeight: 700, fontSize: 12, color: descColor, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {cat.name || 'Catalog'}
                              </Typography>
                              <Chip
                                label={isMulti ? 'Multi' : 'Single'}
                                size="small"
                                sx={{ height: 16, fontSize: 9, backgroundColor: '#25D366', color: '#fff', fontWeight: 600, '& .MuiChip-label': { px: 0.75 } }}
                              />
                            </Box>
                            <Divider sx={{ mb: 0.75 }} />
                            {/* Products count */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                              <InventoryIcon sx={{ color: isDark ? '#bbb' : '#666', fontSize: 13 }} />
                              <Typography sx={{ fontWeight: 600, fontSize: 11, color: descColor }}>
                                Products ({catProducts.length})
                              </Typography>
                            </Box>
                            {/* Show up to 2 products */}
                            {catProducts.slice(0, 2).map((product, pi) => (
                              <Box key={pi} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, p: 0.5, borderRadius: 1, backgroundColor: isDark ? '#2a2a2a' : '#f8f9fa', border: isDark ? '1px solid #444' : '1px solid #e0e0e0', mb: 0.5 }}>
                                {/* Product image thumbnail */}
                                <Box sx={{ width: 32, height: 32, borderRadius: 1, backgroundColor: isDark ? '#333' : '#e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                                  {product.imageUrl && product.imageUrl.startsWith('http') ? (
                                    <img src={product.imageUrl} alt={product.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.style.display = 'none'; }} />
                                  ) : (
                                    <ImageIcon sx={{ fontSize: 16, color: isDark ? '#666' : '#999' }} />
                                  )}
                                </Box>
                                {/* Product info */}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography sx={{ fontWeight: 600, fontSize: 10, color: isDark ? '#fff' : '#222', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {product.name || 'Product'}
                                  </Typography>
                                  {product.price && product.price !== '0' && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                      <AttachMoneyIcon sx={{ fontSize: 10, color: '#25D366' }} />
                                      <Typography sx={{ fontSize: 9, color: '#25D366', fontWeight: 600 }}>
                                        {product.price} {product.currency || 'USD'}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              </Box>
                            ))}
                            {catProducts.length > 2 && (
                              <Typography sx={{ fontSize: 9, color: isDark ? '#bbb' : '#888', textAlign: 'center', mt: 0.25 }}>
                                +{catProducts.length - 2} more
                              </Typography>
                            )}
                          </Box>
                        );
                      }
                      if (field.type === "button" && field.selectedValue) {
                        return (
                          <Box
                            key={sectionIdx + "-" + idx}
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              color: isDark ? "#fff" : "#222",
                              borderRadius: 2,
                              px: 6,
                              py: 3,
                              border: '2px solid #bdbdbd',
                              fontWeight: 600,
                              fontSize: 13,
                              my: 0.5,
                              gap: 1,
                              maxWidth: "100%",
                              overflow: "hidden",
                              wordBreak: "break-word",
                              overflowWrap: "break-word",
                            }}
                          >
                            <ListIcon sx={{ fontSize: 16, mr: 0.5 }} />
                            {field.selectedValue}
                          </Box>
                        );
                      }
                      if (field.type === "file" && field.selectedValue) {
                        const imgVal = field.selectedValue;
                        const urls = (Array.isArray(imgVal) ? imgVal : [imgVal]).filter(u => typeof u === 'string' && u.startsWith('http'));
                        if (urls.length === 0) return null;
                        return (
                          <Box key={sectionIdx + "-" + idx} sx={{ mt: 0.5 }}>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {urls.slice(0, 4).map((url, i) => (
                                <Box
                                  key={i}
                                  sx={{ width: 48, height: 48, borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: isDark ? '#444' : '#e0e0e0', flexShrink: 0, cursor: 'zoom-in', position: 'relative' }}
                                  onClick={(e) => { e.stopPropagation(); window.open(url, '_blank', 'noopener,noreferrer'); }}
                                >
                                  <img src={url} alt={`img-${i}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.style.display = 'none'; }} />
                                  {i === 3 && urls.length > 4 && (
                                    <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                      <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 13 }}>+{urls.length - 3}</Typography>
                                    </Box>
                                  )}
                                </Box>
                              ))}
                            </Box>
                            {urls.length > 1 && (
                              <Typography variant="caption" sx={{ color: descColor, display: 'block', mt: 0.25 }}>
                                {urls.length} image{urls.length > 1 ? 's' : ''}
                              </Typography>
                            )}
                          </Box>
                        );
                      }
                      if (field.type === "template" && field.selectedValue) {
                        const tpl = field.selectedValue;
                        const hasComponents = tpl && Array.isArray(tpl.components) && tpl.components.length > 0;
                        return (
                          <Box key={sectionIdx + "-" + idx} sx={{ width: "100%", mt: 0.5 }}>
                            {hasComponents ? (
                              <TemplateSendPreview singleTemplate={tpl} />
                            ) : (
                              <Box sx={{ px: 1, py: 0.5, fontSize: 12, color: descColor }}>
                                <span style={{ fontWeight: 600 }}>Template:</span> {tpl.name || String(tpl)}
                                {tpl.category && <span style={{ marginLeft: 6, opacity: 0.7 }}>({tpl.category})</span>}
                              </Box>
                            )}
                          </Box>
                        );
                      }
                      return null;
                    })



                    : null
                )}
              </Box>}
              {/* Media template: show expiry badge on canvas */}
              {data.mediaExpiry === "expired" && (
                <Typography variant="caption" sx={{ color: "error.main", fontWeight: 600, mt: 0.5, display: "block" }}>Media expired – re-upload</Typography>
              )}
              {data.mediaExpiry === "expiring_soon" && data.mediaExpiryDays != null && (
                <Typography variant="caption" sx={{ color: "warning.main", fontWeight: 600, mt: 0.5, display: "block" }}>Expires in {data.mediaExpiryDays} days</Typography>
              )}
              {/* Render all variable fields at the end (not for list message or interactive reply buttons) */}
              {heading !== 'Send list message' && heading !== 'Send interactive reply buttons message' && <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  mt: 1,
                  maxWidth: "100%",
                  width: "100%",
                }}
              >
                {data.config.map((section, sectionIdx) =>
                  Array.isArray(section.fields)
                    ? section.fields
                      .filter((f) => f.type === "variable" && f.selectedValue)
                      .map((field, idx) => (
                        <Box
                          key={`var-${sectionIdx}-${idx}`}
                          sx={{
                            bgcolor: isDark ? "#d3d3d3" : "#e0e0e0",
                            color: "#444",
                            borderRadius: 2,
                            px: 1.5,
                            py: 0.5,
                            fontWeight: 500,
                            fontSize: 14,
                            my: 0.5,
                            display: "inline-block",
                            fontFamily: "monospace",
                            maxWidth: "100%",
                            overflow: "hidden",
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                          }}
                        >
                          {"{"}
                          {field.selectedValue}
                          {"}"}
                        </Box>
                      ))
                    : null
                )}
              </Box>}
            </>
          )}
        {data.buttonText && (
          <Box sx={{ mt: 0.5 }}>
            <button
              style={{
                borderRadius: 16,
                padding: "4px 16px",
                fontWeight: 500,
                background: isDark ? "#444" : "#222",
                color: "#fff",
                border: "none",
                boxShadow: isDark ? "0 2px 8px #1118" : "0 2px 8px #0002",
                cursor: "pointer",
                fontSize: 12,
              }}
            >
              {data.buttonText}
            </button>
          </Box>
        )}
      </Box>

      {/* Three dots menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem onClick={handleDelete}>
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default WorkflowNode;